import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { adminAPI, User, getApiBaseUrl } from '../../services/api';
import { 
  UserGroupIcon, 
  TruckIcon, 
  CurrencyDollarIcon, 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UsersIcon,
  ShoppingBagIcon,
  ShieldCheckIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  DocumentMagnifyingGlassIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [userToReject, setUserToReject] = useState<User | null>(null);
  const [viewingDoc, setViewingDoc] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeOrders: 0,
    totalRevenue: 0,
    activeDispatchers: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch both regular pending users and pending corporate approvals
      const [regularUsers, corporateUsers, statsData] = await Promise.all([
        adminAPI.getPendingUsers(),
        adminAPI.getPendingCorporateApprovals(),
        adminAPI.getStats()
      ]);
      
      // Combine both lists into unified pending approvals
      const allPendingUsers = [...regularUsers, ...corporateUsers];
      setPendingUsers(allPendingUsers);
      
      setStats({
        totalUsers: statsData.total_users,
        activeOrders: statsData.active_orders,
        totalRevenue: statsData.total_revenue,
        activeDispatchers: statsData.active_dispatchers
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId: string) => {
    setActionLoading(userId);
    try {
      await adminAPI.approveUser(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      
      // Refresh stats and pending users after approval
      const [regularUsers, corporateUsers, statsData] = await Promise.all([
        adminAPI.getPendingUsers(),
        adminAPI.getPendingCorporateApprovals(),
        adminAPI.getStats()
      ]);
      setPendingUsers([...regularUsers, ...corporateUsers]);
      setStats({
        totalUsers: statsData.total_users,
        activeOrders: statsData.active_orders,
        totalRevenue: statsData.total_revenue,
        activeDispatchers: statsData.active_dispatchers
      });
    } catch (error) {
      console.error('Error approving user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectUser = async (userId: string) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(userId);
    try {
      await adminAPI.rejectUser(userId, rejectionReason);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      setShowRejectModal(false);
      setRejectionReason('');
      setUserToReject(null);
      
      // Refresh stats and pending users after rejection
      const [regularUsers, corporateUsers, statsData] = await Promise.all([
        adminAPI.getPendingUsers(),
        adminAPI.getPendingCorporateApprovals(),
        adminAPI.getStats()
      ]);
      setPendingUsers([...regularUsers, ...corporateUsers]);
      setStats({
        totalUsers: statsData.total_users,
        activeOrders: statsData.active_orders,
        totalRevenue: statsData.total_revenue,
        activeDispatchers: statsData.active_dispatchers
      });
    } catch (error) {
      console.error('Error rejecting user:', error);
    } finally {
      setActionLoading(null);
    }
  };
  
  const openRejectModal = (user: User) => {
    setUserToReject(user);
    setShowRejectModal(true);
  };
  
  const openDetailsModal = (user: User) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const getRoleIcon = (role: string, customerType?: string) => {
    if (customerType === 'corporate') {
      return <BuildingOffice2Icon className="h-5 w-5 text-orange-600" />;
    }
    switch (role) {
      case 'rider':
        return <TruckIcon className="h-5 w-5 text-volexx-blue-600" />;
      case 'merchant':
        return <ShoppingBagIcon className="h-5 w-5 text-purple-600" />;
      case 'customer':
        return <UserGroupIcon className="h-5 w-5 text-green-600" />;
      default:
        return <UserGroupIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getRoleBadgeColor = (role: string, customerType?: string) => {
    if (customerType === 'corporate') {
      return 'bg-orange-100 text-orange-800';
    }
    switch (role) {
      case 'rider':
        return 'bg-volexx-blue-100 text-volexx-blue-800';
      case 'merchant':
        return 'bg-purple-100 text-purple-800';
      case 'customer':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getApprovalTypeBadge = (user: User) => {
    if (user.customer_type === 'corporate') {
      return 'Corporate Customer';
    }
    if (user.role === 'rider') {
      return 'Dispatcher';
    }
    if (user.role === 'merchant') {
      return 'Merchant';
    }
    return user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Unknown';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-volexx-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-poppins">Admin Dashboard</h1>
          <p className="mt-2 text-gray-600 font-poppins">Welcome, {user?.name}! Manage the Volexx platform.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <button 
            onClick={() => window.scrollTo({ top: document.getElementById('pending-approvals')?.offsetTop || 0, behavior: 'smooth' })}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer text-left w-full"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Pending Approvals</p>
                <p className="mt-2 text-3xl font-bold text-yellow-600 font-poppins">{pendingUsers.length}</p>
                <p className="mt-1 text-xs text-gray-500">Requires action</p>
              </div>
              <div className="bg-yellow-100 rounded-volexx p-3">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
              </div>
            </div>
          </button>

          <button 
            onClick={() => navigate('/admin/customers')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer text-left w-full"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Total Users</p>
                <p className="mt-2 text-3xl font-bold text-volexx-blue-600 font-poppins">{stats.totalUsers}</p>
                <p className="mt-1 text-xs text-green-600">↑ 12% from last month</p>
              </div>
              <div className="bg-volexx-blue-100 rounded-volexx p-3">
                <UserGroupIcon className="h-8 w-8 text-volexx-blue-600" />
              </div>
            </div>
          </button>

          <button 
            onClick={() => navigate('/admin/orders')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer text-left w-full"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Active Orders</p>
                <p className="mt-2 text-3xl font-bold text-volexx-blue-600 font-poppins">{stats.activeOrders}</p>
                <p className="mt-1 text-xs text-gray-500">{stats.activeDispatchers} dispatchers active</p>
              </div>
              <div className="bg-volexx-blue-100 rounded-volexx p-3">
                <TruckIcon className="h-8 w-8 text-volexx-blue-600" />
              </div>
            </div>
          </button>

          <button 
            onClick={() => navigate('/admin/orders')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg transition-all hover:scale-105 cursor-pointer text-left w-full"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Platform Revenue</p>
                <p className="mt-2 text-3xl font-bold text-green-600 font-poppins">{formatPrice(stats.totalRevenue)}</p>
                <p className="mt-1 text-xs text-green-600">↑ 8% from last month</p>
              </div>
              <div className="bg-green-100 rounded-volexx p-3">
                <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </button>
        </div>

        {/* Pending Approvals Section */}
        <div id="pending-approvals" className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mr-2" />
                <h2 className="text-xl font-bold text-gray-900 font-poppins">Pending Approvals</h2>
              </div>
              {pendingUsers.length > 0 && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-volexx">
                  {pendingUsers.length} pending
                </span>
              )}
            </div>
          </div>
          
          {pendingUsers.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 font-poppins mb-2">All caught up!</h3>
              <p className="text-gray-600 font-poppins">No pending user approvals at the moment.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingUsers.map((pendingUser) => (
                    <tr key={pendingUser.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            {pendingUser.customer_type === 'corporate' && pendingUser.company_logo_url ? (
                              <img 
                                src={`${getApiBaseUrl()}/${pendingUser.company_logo_url}`}
                                alt="Company Logo"
                                className="h-10 w-10 rounded-volexx object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                }}
                              />
                            ) : null}
                            <div className={`h-10 w-10 rounded-volexx ${pendingUser.customer_type === 'corporate' ? 'bg-orange-100' : 'bg-volexx-blue-100'} flex items-center justify-center ${pendingUser.customer_type === 'corporate' && pendingUser.company_logo_url ? 'hidden' : ''}`}>
                              <span className={`${pendingUser.customer_type === 'corporate' ? 'text-orange-600' : 'text-volexx-blue-600'} font-bold font-poppins`}>
                                {pendingUser.customer_type === 'corporate' 
                                  ? (pendingUser.company_name?.[0] || 'C')
                                  : (pendingUser.first_name?.[0] || '') + (pendingUser.last_name?.[0] || '')}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-bold text-gray-900 font-poppins">
                              {pendingUser.customer_type === 'corporate' 
                                ? pendingUser.company_name 
                                : `${pendingUser.first_name} ${pendingUser.last_name}`}
                            </div>
                            {pendingUser.customer_type === 'corporate' && (
                              <div className="text-xs text-orange-600 font-poppins flex items-center">
                                <BuildingOffice2Icon className="h-3 w-3 mr-1" />
                                Corporate • {pendingUser.first_name} {pendingUser.last_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getRoleIcon(pendingUser.role, pendingUser.customer_type)}
                          <span className={`ml-2 px-3 py-1 text-xs font-semibold rounded-volexx font-poppins ${getRoleBadgeColor(pendingUser.role, pendingUser.customer_type)}`}>
                            {getApprovalTypeBadge(pendingUser)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-poppins">{pendingUser.email}</div>
                        <div className="text-sm text-gray-500 font-poppins">{pendingUser.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-poppins">
                        {pendingUser.location ? (
                          <span>
                            {pendingUser.location.latitude.toFixed(4)}, {pendingUser.location.longitude.toFixed(4)}
                          </span>
                        ) : (
                          <span>N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openDetailsModal(pendingUser)}
                            className="inline-flex items-center px-3 py-2 border border-volexx-blue-300 text-sm font-bold rounded-volexx text-volexx-blue-700 bg-volexx-blue-50 hover:bg-volexx-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-volexx-blue-500 transition-colors font-poppins"
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => handleApproveUser(pendingUser.id)}
                            disabled={actionLoading === pendingUser.id}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-bold rounded-volexx text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors font-poppins"
                          >
                            {actionLoading === pendingUser.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            ) : (
                              <CheckCircleIcon className="h-4 w-4 mr-2" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => openRejectModal(pendingUser)}
                            disabled={actionLoading === pendingUser.id}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-bold rounded-volexx text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-colors font-poppins"
                          >
                            <XCircleIcon className="h-4 w-4 mr-2" />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Access Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <button
            onClick={() => navigate('/admin/customers')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-green-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-green-100 rounded-volexx p-3 group-hover:bg-green-200 transition-colors">
                <UserGroupIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Customer Management</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">View and manage all platform customers</p>
            <span className="text-green-600 font-semibold font-poppins group-hover:text-green-700">
              Manage Customers →
            </span>
          </button>

          <button
            onClick={() => navigate('/admin/orders')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-volexx-blue-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-volexx-blue-100 rounded-volexx p-3 group-hover:bg-volexx-blue-200 transition-colors">
                <TruckIcon className="h-8 w-8 text-volexx-blue-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Order Management</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Monitor all orders and deliveries</p>
            <span className="text-volexx-blue-600 font-semibold font-poppins group-hover:text-volexx-blue-700">
              View Orders →
            </span>
          </button>

          <button
            onClick={() => navigate('/admin/dispatchers')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-volexx-blue-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-volexx-blue-100 rounded-volexx p-3 group-hover:bg-volexx-blue-200 transition-colors">
                <UserGroupIcon className="h-8 w-8 text-volexx-blue-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Dispatcher Management</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Monitor dispatcher allocation and performance</p>
            <span className="text-volexx-blue-600 font-semibold font-poppins group-hover:text-volexx-blue-700">
              Manage Dispatchers →
            </span>
          </button>

          <button
            onClick={() => navigate('/admin/security')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-indigo-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-indigo-100 rounded-volexx p-3 group-hover:bg-indigo-200 transition-colors">
                <LockClosedIcon className="h-8 w-8 text-indigo-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Security Settings</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Enable two-factor authentication (2FA) for your account</p>
            <span className="text-indigo-600 font-semibold font-poppins group-hover:text-indigo-700">
              Manage Security →
            </span>
          </button>

          <div
            className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-volexx-lg shadow-volexx border border-orange-200 p-6 hover:shadow-lg transition-all col-span-1 md:col-span-2"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="bg-orange-100 rounded-volexx p-3">
                  <ChatBubbleLeftRightIcon className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-bold text-gray-900 font-poppins">Communication Hub</h3>
                  <p className="text-sm text-gray-600 font-poppins">Message dispatchers & send announcements</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => navigate('/admin/communication')}
                className="flex items-center gap-3 bg-white rounded-lg p-4 border border-gray-200 hover:border-sky-400 hover:bg-sky-50 transition-all group"
              >
                <div className="bg-sky-100 rounded-lg p-2 group-hover:bg-sky-200 transition-colors">
                  <svg className="w-5 h-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 text-sm">Conversations</div>
                  <div className="text-xs text-gray-500">Direct messages</div>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/admin/communication?tab=broadcast')}
                className="flex items-center gap-3 bg-white rounded-lg p-4 border border-gray-200 hover:border-orange-400 hover:bg-orange-50 transition-all group"
              >
                <div className="bg-orange-100 rounded-lg p-2 group-hover:bg-orange-200 transition-colors">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 text-sm">Broadcast</div>
                  <div className="text-xs text-gray-500">Send announcements</div>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/admin/communication?tab=announcements')}
                className="flex items-center gap-3 bg-white rounded-lg p-4 border border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all group"
              >
                <div className="bg-purple-100 rounded-lg p-2 group-hover:bg-purple-200 transition-colors">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 text-sm">History</div>
                  <div className="text-xs text-gray-500">Past announcements</div>
                </div>
              </button>
            </div>
          </div>

          <button
            onClick={() => navigate('/admin/analytics')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-volexx-blue-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-green-100 rounded-volexx p-3 group-hover:bg-green-200 transition-colors">
                <ChartBarIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Platform Analytics</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">View revenue and usage statistics</p>
            <span className="text-green-600 font-semibold font-poppins group-hover:text-green-700">
              View Analytics →
            </span>
          </button>

          <button
            onClick={() => navigate('/admin/settings')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-volexx-blue-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-gray-100 rounded-volexx p-3 group-hover:bg-gray-200 transition-colors">
                <Cog6ToothIcon className="h-8 w-8 text-gray-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">System Settings</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Configure platform settings and preferences</p>
            <span className="text-gray-600 font-semibold font-poppins group-hover:text-gray-700">
              View Settings →
            </span>
          </button>

          {user?.is_super_admin && (
            <button
              onClick={() => navigate('/admin/admins')}
              className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-purple-300 transition-all text-left group"
            >
              <div className="flex items-center mb-4">
                <div className="bg-purple-100 rounded-volexx p-3 group-hover:bg-purple-200 transition-colors">
                  <ShieldCheckIcon className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Admin Management</h3>
              </div>
              <p className="text-gray-600 mb-4 font-poppins">Create and manage platform administrators</p>
              <span className="text-purple-600 font-semibold font-poppins group-hover:text-purple-700">
                Manage Admins →
              </span>
            </button>
          )}

          <button
            onClick={() => navigate('/admin/pricing')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-volexx-orange-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-orange-100 rounded-volexx p-3 group-hover:bg-orange-200 transition-colors">
                <svg className="h-8 w-8 text-volexx-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Pricing Management</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Configure vehicle delivery pricing and rates</p>
            <span className="text-volexx-orange-600 font-semibold font-poppins group-hover:text-volexx-orange-700">
              Manage Pricing →
            </span>
          </button>

          <button
            onClick={() => navigate('/admin/wallets')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-green-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-green-100 rounded-volexx p-3 group-hover:bg-green-200 transition-colors">
                <BanknotesIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Wallet Management</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Manage user wallets and transactions</p>
            <span className="text-green-600 font-semibold font-poppins group-hover:text-green-700">
              Manage Wallets →
            </span>
          </button>

          <button
            onClick={() => navigate('/admin/activity-log')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-blue-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 rounded-volexx p-3 group-hover:bg-blue-200 transition-colors">
                <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Admin Activity Log</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">View all admin actions and accountability</p>
            <span className="text-blue-600 font-semibold font-poppins group-hover:text-blue-700">
              View Activity Log →
            </span>
          </button>


          <button
            onClick={() => navigate('/admin/support')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-indigo-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-indigo-100 rounded-volexx p-3 group-hover:bg-indigo-200 transition-colors">
                <ChatBubbleLeftRightIcon className="h-8 w-8 text-indigo-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Support Center</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Receive and respond to user support messages</p>
            <span className="text-indigo-600 font-semibold font-poppins group-hover:text-indigo-700">
              Manage Support Tickets →
            </span>
          </button>

          <button
            onClick={() => navigate('/admin/merchants')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-purple-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-purple-100 rounded-volexx p-3 group-hover:bg-purple-200 transition-colors">
                <ShoppingBagIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Merchant Management</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Oversee all merchant accounts and analytics</p>
            <span className="text-purple-600 font-semibold font-poppins group-hover:text-purple-700">
              Manage Merchants →
            </span>
          </button>

          <button
            onClick={() => navigate('/admin/dispatcher-assignment')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-teal-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-teal-100 rounded-volexx p-3 group-hover:bg-teal-200 transition-colors">
                <UsersIcon className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Dispatcher Assignment</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Assign dispatchers to merchants</p>
            <span className="text-teal-600 font-semibold font-poppins group-hover:text-teal-700">
              Manage Assignments →
            </span>
          </button>

          <button
            onClick={() => navigate('/admin/luxury-vehicles')}
            className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 hover:shadow-lg hover:border-amber-300 transition-all text-left group"
          >
            <div className="flex items-center mb-4">
              <div className="bg-amber-100 rounded-volexx p-3 group-hover:bg-amber-200 transition-colors">
                <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-bold text-gray-900 font-poppins">Luxury Vehicle Hire</h3>
            </div>
            <p className="text-gray-600 mb-4 font-poppins">Manage premium vehicles, pricing, and bookings</p>
            <span className="text-amber-600 font-semibold font-poppins group-hover:text-amber-700">
              Manage Luxury Fleet →
            </span>
          </button>
        </div>
      </div>

      {/* User Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowDetailsModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 font-poppins" id="modal-title">
                        {selectedUser?.customer_type === 'corporate' 
                          ? selectedUser?.company_name 
                          : selectedUser?.role === 'rider' 
                            ? 'Dispatcher' 
                            : selectedUser?.role 
                              ? selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1) 
                              : 'User'} Details
                        {selectedUser?.customer_type === 'corporate' && (
                          <span className="ml-2 px-2 py-1 text-xs bg-orange-100 text-orange-600 rounded-volexx">Corporate</span>
                        )}
                      </h3>
                      <button
                        onClick={() => setShowDetailsModal(false)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <XCircleIcon className="h-6 w-6" />
                      </button>
                    </div>
                    
                    {/* Dispatcher Photos Section (for dispatchers only) */}
                    {selectedUser.role === 'rider' && (selectedUser.rider_photo_url || selectedUser.vehicle?.vehicle_photo_url) && (
                      <div className="mb-6">
                        <h4 className="font-bold text-gray-900 mb-4 font-poppins">Verification Photos</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedUser.rider_photo_url && (
                            <div className="bg-gray-50 rounded-volexx p-4">
                              <p className="text-sm font-semibold text-gray-700 mb-2 font-poppins">Rider Photo</p>
                              <div className="w-full h-64 flex items-center justify-center bg-white rounded-volexx border border-gray-200">
                                <img 
                                  src={`${getApiBaseUrl()}/${selectedUser.rider_photo_url}`} 
                                  alt="Rider"
                                  className="max-w-full max-h-full object-contain rounded-volexx"
                                  onError={(e) => {
                                    e.currentTarget.src = 'https://via.placeholder.com/300x200?text=Photo+Not+Available';
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          {selectedUser.vehicle?.vehicle_photo_url && (
                            <div className="bg-gray-50 rounded-volexx p-4">
                              <p className="text-sm font-semibold text-gray-700 mb-2 font-poppins">Vehicle Photo</p>
                              <div className="w-full h-64 flex items-center justify-center bg-white rounded-volexx border border-gray-200">
                                <img 
                                  src={`${getApiBaseUrl()}/${selectedUser.vehicle.vehicle_photo_url}`} 
                                  alt="Vehicle"
                                  className="max-w-full max-h-full object-contain rounded-volexx"
                                  onError={(e) => {
                                    e.currentTarget.src = 'https://via.placeholder.com/300x200?text=Photo+Not+Available';
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Personal Information */}
                      <div className="bg-gray-50 rounded-volexx p-4">
                        <h4 className="font-bold text-gray-900 mb-3 font-poppins">Personal Information</h4>
                        <div className="space-y-2">
                          <div><span className="text-gray-600">Name:</span> <span className="font-semibold">{selectedUser.first_name} {selectedUser.last_name}</span></div>
                          <div><span className="text-gray-600">Email:</span> <span className="font-semibold">{selectedUser.email}</span></div>
                          <div><span className="text-gray-600">Phone:</span> <span className="font-semibold">{selectedUser.phone}</span></div>
                          <div><span className="text-gray-600">Role:</span> <span className="font-semibold">{selectedUser.role === 'rider' ? 'Dispatcher' : selectedUser.role}</span></div>
                        </div>
                      </div>
                      
                      {/* Vehicle Information (for dispatchers only) */}
                      {selectedUser.role === 'rider' && selectedUser.vehicle && (
                        <div className="bg-blue-50 rounded-volexx p-4">
                          <h4 className="font-bold text-gray-900 mb-3 font-poppins">Vehicle Information</h4>
                          <div className="space-y-2">
                            <div><span className="text-gray-600">Type:</span> <span className="font-semibold capitalize">{selectedUser.vehicle.vehicle_type}</span></div>
                            <div><span className="text-gray-600">License Plate:</span> <span className="font-semibold">{selectedUser.vehicle.license_plate || 'N/A (Bicycle)'}</span></div>
                            <div><span className="text-gray-600">Make:</span> <span className="font-semibold">{selectedUser.vehicle.make || 'N/A'}</span></div>
                            <div><span className="text-gray-600">Model:</span> <span className="font-semibold">{selectedUser.vehicle.model || 'N/A'}</span></div>
                            <div><span className="text-gray-600">Year:</span> <span className="font-semibold">{selectedUser.vehicle.year || 'N/A'}</span></div>
                            <div><span className="text-gray-600">Color:</span> <span className="font-semibold">{selectedUser.vehicle.color || 'N/A'}</span></div>
                            <div><span className="text-gray-600">Capacity:</span> <span className="font-semibold">{selectedUser.vehicle.capacity_kg} kg</span></div>
                          </div>
                        </div>
                      )}
                      
                      {/* Bank Information (for dispatchers only) */}
                      {selectedUser.role === 'rider' && selectedUser.bank_info && (
                        <div className="bg-green-50 rounded-volexx p-4">
                          <h4 className="font-bold text-gray-900 mb-3 font-poppins">Bank Information</h4>
                          <div className="space-y-2">
                            <div><span className="text-gray-600">Bank Name:</span> <span className="font-semibold">{selectedUser.bank_info.bank_name}</span></div>
                            <div><span className="text-gray-600">Account Number:</span> <span className="font-semibold">{selectedUser.bank_info.account_number}</span></div>
                            <div><span className="text-gray-600">Account Name:</span> <span className="font-semibold">{selectedUser.bank_info.account_name}</span></div>
                          </div>
                        </div>
                      )}
                      
                      {/* Business Information (for merchants only) */}
                      {selectedUser.role === 'merchant' && selectedUser.business && (
                        <div className="bg-purple-50 rounded-volexx p-4">
                          <h4 className="font-bold text-gray-900 mb-3 font-poppins">Business Information</h4>
                          <div className="space-y-2">
                            <div><span className="text-gray-600">Business Name:</span> <span className="font-semibold">{selectedUser.business.name || 'N/A'}</span></div>
                            <div><span className="text-gray-600">Business Address:</span> <span className="font-semibold">{selectedUser.business.address || 'N/A'}</span></div>
                          </div>
                        </div>
                      )}
                      
                      {/* Corporate Information (for corporate customers only) */}
                      {selectedUser.customer_type === 'corporate' && (
                        <div className="bg-orange-50 rounded-volexx p-4">
                          <h4 className="font-bold text-gray-900 mb-3 font-poppins flex items-center">
                            <BuildingOffice2Icon className="h-5 w-5 mr-2 text-orange-600" />
                            Corporate Information
                          </h4>
                          <div className="space-y-2">
                            <div><span className="text-gray-600">Company Name:</span> <span className="font-semibold">{selectedUser.company_name || 'N/A'}</span></div>
                            <div><span className="text-gray-600">CAC Registration Number:</span> <span className="font-semibold">{selectedUser.company_registration_number || 'N/A'}</span></div>
                            <div><span className="text-gray-600">Business Address:</span> <span className="font-semibold">{selectedUser.company_address || 'N/A'}</span></div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Corporate Documents Section (for corporate customers only) */}
                    {selectedUser.customer_type === 'corporate' && (selectedUser.company_registration_doc_url || selectedUser.company_logo_url) && (
                      <div className="mt-6">
                        <h4 className="font-bold text-gray-900 mb-4 font-poppins flex items-center">
                          <ClipboardDocumentListIcon className="h-5 w-5 mr-2 text-orange-600" />
                          Uploaded Documents
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedUser.company_registration_doc_url && (
                            <div className="bg-gray-50 rounded-volexx p-4">
                              <p className="text-sm font-semibold text-gray-700 mb-2 font-poppins">CAC Registration Certificate</p>
                              <div className="w-full h-64 flex items-center justify-center bg-white rounded-volexx border border-gray-200">
                                <img 
                                  src={`${getApiBaseUrl()}/${selectedUser.company_registration_doc_url}`} 
                                  alt="CAC Certificate"
                                  className="max-w-full max-h-full object-contain rounded-volexx"
                                  onError={(e) => {
                                    e.currentTarget.src = 'https://via.placeholder.com/300x200?text=Document+Not+Available';
                                  }}
                                />
                              </div>
                              <div className="mt-2 flex space-x-2">
                                <a 
                                  href={`${getApiBaseUrl()}/${selectedUser.company_registration_doc_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-1 text-center px-3 py-2 bg-volexx-blue-600 text-white text-sm rounded-volexx hover:bg-volexx-blue-700 transition-colors font-poppins"
                                >
                                  View Full Size
                                </a>
                                <a 
                                  href={`${getApiBaseUrl()}/${selectedUser.company_registration_doc_url}`}
                                  download
                                  className="flex-1 text-center px-3 py-2 bg-gray-600 text-white text-sm rounded-volexx hover:bg-gray-700 transition-colors font-poppins"
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          )}
                          {selectedUser.company_logo_url && (
                            <div className="bg-gray-50 rounded-volexx p-4">
                              <p className="text-sm font-semibold text-gray-700 mb-2 font-poppins">Company Logo</p>
                              <div className="w-full h-64 flex items-center justify-center bg-white rounded-volexx border border-gray-200">
                                <img 
                                  src={`${getApiBaseUrl()}/${selectedUser.company_logo_url}`} 
                                  alt="Company Logo"
                                  className="max-w-full max-h-full object-contain rounded-volexx"
                                  onError={(e) => {
                                    e.currentTarget.src = 'https://via.placeholder.com/300x200?text=Logo+Not+Available';
                                  }}
                                />
                              </div>
                              <div className="mt-2">
                                <a 
                                  href={`${getApiBaseUrl()}/${selectedUser.company_logo_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block text-center px-3 py-2 bg-volexx-blue-600 text-white text-sm rounded-volexx hover:bg-volexx-blue-700 transition-colors font-poppins"
                                >
                                  View Full Size
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="w-full sm:w-auto inline-flex justify-center rounded-volexx border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-bold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-volexx-blue-500 sm:ml-3 font-poppins"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showRejectModal && userToReject && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowRejectModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-volexx bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-bold text-gray-900 font-poppins" id="modal-title">
                      Reject {userToReject?.role === 'rider' ? 'Dispatcher' : userToReject?.role ? userToReject.role.charAt(0).toUpperCase() + userToReject.role.slice(1) : 'User'}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 font-poppins mb-3">
                        Please provide a reason for rejecting {userToReject.first_name} {userToReject.last_name}:
                      </p>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 font-poppins"
                        placeholder="e.g., Incomplete documentation, Invalid license plate, etc."
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => handleRejectUser(userToReject.id)}
                  disabled={actionLoading === userToReject.id || !rejectionReason.trim()}
                  className="w-full inline-flex justify-center rounded-volexx border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-bold text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                >
                  {actionLoading === userToReject.id ? 'Rejecting...' : 'Reject User'}
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                    setUserToReject(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-volexx border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-bold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-volexx-blue-500 sm:mt-0 sm:w-auto font-poppins"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
